import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function getMonthOptions() {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('bg-BG', { year: 'numeric', month: 'long' });
    months.push({ value, label });
  }
  return months;
}

export const PaymentsTab = ({ employees }) => {
  const [additionalPayments, setAdditionalPayments] = useState([]);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ employee_id: '', amount: '', reason: '', month: getCurrentMonth() });

  useEffect(() => { loadAdditionalPayments(); }, []);

  const loadAdditionalPayments = async () => {
    try {
      const response = await axios.get(`${API}/additional-payments`);
      setAdditionalPayments(response.data);
    } catch {
      console.error('Error loading payments');
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/additional-payments`, {
        employee_id: paymentForm.employee_id,
        amount: parseFloat(paymentForm.amount),
        reason: paymentForm.reason || null,
        month: paymentForm.month
      });
      setShowPaymentDialog(false);
      setPaymentForm({ employee_id: '', amount: '', reason: '', month: getCurrentMonth() });
      loadAdditionalPayments();
      toast.success('Доплащането е добавено');
    } catch {
      toast.error('Грешка при добавяне');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Сигурни ли сте?')) return;
    try {
      await axios.delete(`${API}/additional-payments/${paymentId}`);
      loadAdditionalPayments();
      toast.success('Доплащането е изтрито');
    } catch {
      toast.error('Грешка при изтриване');
    }
  };

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="heading-font text-xl font-bold uppercase">Доплащания</h2>
        <Button data-testid="add-payment-button" onClick={() => { setPaymentForm({ employee_id: '', amount: '', reason: '', month: getCurrentMonth() }); setShowPaymentDialog(true); }} className="bg-green-600 hover:bg-green-700 font-bold uppercase">
          <Plus className="w-5 h-5 mr-2" />Добави доплащане
        </Button>
      </div>

      {additionalPayments.length === 0 ? (
        <div className="text-center py-12 text-slate-500">Няма добавени доплащания</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="uppercase text-xs">Служител</TableHead>
              <TableHead className="uppercase text-xs">Месец</TableHead>
              <TableHead className="uppercase text-xs">Сума</TableHead>
              <TableHead className="uppercase text-xs">Причина</TableHead>
              <TableHead className="uppercase text-xs">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {additionalPayments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell>{payment.employee_name || '-'}</TableCell>
                <TableCell className="mono-font">{payment.month}</TableCell>
                <TableCell className="mono-font font-bold text-green-600">{payment.amount} EUR</TableCell>
                <TableCell>{payment.reason || '-'}</TableCell>
                <TableCell>
                  <Button onClick={() => handleDeletePayment(payment.id)} variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="heading-font text-xl uppercase">Добави доплащане</DialogTitle></DialogHeader>
          <form onSubmit={handleAddPayment} className="space-y-4">
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Служител *</Label>
              <Select value={paymentForm.employee_id} onValueChange={(v) => setPaymentForm({ ...paymentForm, employee_id: v })}>
                <SelectTrigger><SelectValue placeholder="Изберете служител" /></SelectTrigger>
                <SelectContent>{employees.map(emp => <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Месец *</Label>
              <Select value={paymentForm.month} onValueChange={(v) => setPaymentForm({ ...paymentForm, month: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{getMonthOptions().map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Сума (EUR) *</Label>
              <Input type="number" step="0.01" min="0" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} className="border-2" placeholder="0.00" required />
            </div>
            <div>
              <Label className="text-sm uppercase tracking-wide mb-2 block">Причина</Label>
              <Input type="text" value={paymentForm.reason} onChange={(e) => setPaymentForm({ ...paymentForm, reason: e.target.value })} className="border-2" placeholder="Незадължително" />
            </div>
            <div className="flex gap-3">
              <Button type="submit" disabled={!paymentForm.employee_id || !paymentForm.amount || !paymentForm.month} className="flex-1 bg-green-600 hover:bg-green-700 font-bold uppercase">Добави</Button>
              <Button type="button" onClick={() => setShowPaymentDialog(false)} variant="outline" className="flex-1 border-2 font-semibold uppercase">Отказ</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
